import React, { useMemo, useState } from 'react';
import { Button, Form, ProgressBar } from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import SourceUploader from './SourceUploader';
import ManualTagInput from './ManualTagInput';
import SignalChip, { getSourceLabel } from './SignalChip';
import SourceItem from './SourceItem';
import ContextReadinessMeter from './ContextReadinessMeter';
import { ContextSource, Signal, SignalCategory, ReadinessBreakdown, LiveSuggestion } from '../../types/context.types';
import { useUsage } from '../../contexts/UsageContext';
import { PLAN_LIMITS } from '../../types/usage.types';
import './ProjectBrainModal.css';

interface PendingFile {
  file: File;
  name: string;
  type: string;
  size: number;
}

interface ManualTag {
  label: string;
  category: SignalCategory;
}

interface ProjectBrainModalProps {
  onComplete: (result: {
    projectName: string;
    files: Array<{ fileName: string; fileType: string; fileSize: number; data: string }>;
    manualTags: ManualTag[];
  }) => Promise<{
    sources: ContextSource[];
    signals: Signal[];
    readiness: { score: number; breakdown: ReadinessBreakdown; suggestions: LiveSuggestion[] };
  }>;
  onContinue: (projectName: string) => void;
  processing: boolean;
  continueButtonText?: string;
  showProjectName?: boolean;
  existingContext?: {
    sources: ContextSource[];
    signals: Signal[];
    readinessScore: number;
    readinessBreakdown: ReadinessBreakdown;
  };
  onDeleteSource?: (sourceId: string) => Promise<void>;
  onDeleteSignal?: (signalId: string) => Promise<void>;
  onAddManualSignal?: (label: string, category: SignalCategory) => Promise<void>;
  onAddSource?: () => void;
}

const PROCESSING_MESSAGES = [
  'Analyzing documents...',
  'Extracting signals...',
  'Building context...',
  'Almost there...',
];

const ProjectBrainModal: React.FC<ProjectBrainModalProps> = ({
  onComplete,
  onContinue,
  processing: externalProcessing,
  continueButtonText = 'Continue to Home',
  showProjectName = false,
  existingContext,
  onDeleteSource,
  onDeleteSignal,
  onAddManualSignal,
  onAddSource,
}) => {
  const { usageStats } = useUsage();
  const planName = usageStats?.plan || 'starter';
  const sourcesPerProjectLimit = PLAN_LIMITS[planName].sourcesPerProject ?? 5;

  const sourceLimitReachedMessage: React.ReactNode = (() => {
    if (planName === 'starter') {
      return (
        <>
          You've filled all {sourcesPerProjectLimit} source slots on the free plan.{' '}
          <Link to="/select-plan">Upgrade to Early Adopter</Link> for more sources per project.
        </>
      );
    }
    if (planName === 'pro') {
      return (
        <>
          You've filled all {sourcesPerProjectLimit} source slots on Pro.{' '}
          <Link to="/select-plan">Upgrade to Studio</Link> for more sources per project.
        </>
      );
    }
    return <>You've filled all {sourcesPerProjectLimit} source slots for this project.</>;
  })();

  const hasExistingContext =
    !!existingContext &&
    (existingContext.sources.length > 0 || existingContext.signals.length > 0);

  const [step, setStep] = useState<'upload' | 'processing' | 'review' | 'manage'>(
    hasExistingContext ? 'manage' : 'upload'
  );
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [manualTags, setManualTags] = useState<ManualTag[]>([]);
  const [projectName, setProjectName] = useState('');
  const [processingMessage, setProcessingMessage] = useState(PROCESSING_MESSAGES[0]);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [sources, setSources] = useState<ContextSource[]>([]);
  const [readinessScore, setReadinessScore] = useState(0);
  const [readinessBreakdown, setReadinessBreakdown] = useState<ReadinessBreakdown | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const sourceMap = useMemo(() => new Map(sources.map((s) => [s.id, s])), [sources]);
  const existingSourceMap = useMemo(
    () => new Map((existingContext?.sources ?? []).map((s) => [s.id, s])),
    [existingContext?.sources]
  );

  const handleFilesSelected = (files: PendingFile[]) => {
    setPendingFiles(files);
  };

  const handleTagAdd = (label: string, category: SignalCategory) => {
    setManualTags((prev) => [...prev, { label, category }]);
  };

  const handleTagRemove = (index: number) => {
    setManualTags((prev) => prev.filter((_, i) => i !== index));
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleContinueToProcess = async () => {
    if (pendingFiles.length === 0 && manualTags.length === 0) {
      onContinue(projectName.trim() || 'My First Project');
      return;
    }

    setStep('processing');

    // Cycle through processing messages
    let msgIndex = 0;
    const msgInterval = setInterval(() => {
      msgIndex = (msgIndex + 1) % PROCESSING_MESSAGES.length;
      setProcessingMessage(PROCESSING_MESSAGES[msgIndex]);
    }, 2500);

    try {
      // Convert files to base64
      const filesWithData = await Promise.all(
        pendingFiles.map(async (pf) => ({
          fileName: pf.name,
          fileType: pf.type,
          fileSize: pf.size,
          data: await fileToBase64(pf.file),
        }))
      );

      const result = await onComplete({
        projectName: projectName.trim() || 'My First Project',
        files: filesWithData,
        manualTags,
      });

      setSources(result.sources);
      setSignals(result.signals);
      setReadinessScore(result.readiness.score);
      setReadinessBreakdown(result.readiness.breakdown);
      setStep('review');
    } catch (err: any) {
      console.error('Error processing onboarding:', err);
      // Treat client-side aborts (ECONNABORTED), Cloud Run mid-request cutoffs
      // (ERR_NETWORK — connection severed before any response), and explicit
      // 504s as the same "took too long" failure mode.
      const isTimeoutLike =
        err?.code === 'ECONNABORTED' ||
        err?.code === 'ERR_NETWORK' ||
        err?.response?.status === 504 ||
        err?.message?.toLowerCase().includes('timeout');
      if (isTimeoutLike) {
        setProcessingError('Processing took too long. Your documents were uploaded — signals may appear shortly.');
      } else {
        setProcessingError('Something went wrong while processing your documents. You can try again from the sidebar.');
      }
      setStep('review');
    } finally {
      clearInterval(msgInterval);
    }
  };

  if (step === 'upload') {
    const hasContent = pendingFiles.length > 0 || manualTags.length > 0;
    const canContinue = !hasContent || !showProjectName || projectName.trim().length > 0;

    return (
      <div className="project-brain-step">
        <h2 className="project-brain-title">train your ai strategist</h2>
        <p className="project-brain-subtitle">
          Upload existing research docs (PRDs, transcripts, briefs) and add manual tags to give your
          AI assistant rich context about your project.
        </p>

        {showProjectName && (
          <div className="project-brain-section">
            <h6 className="project-brain-section-label">Project Name</h6>
            <Form.Control
              className="project-brain-project-name-input"
              type="text"
              placeholder="e.g., Mobile App Redesign"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              maxLength={100}
            />
          </div>
        )}

        <div className="project-brain-section">
          <h6 className="project-brain-section-label">Upload Documents</h6>
          <SourceUploader
            onFilesSelected={handleFilesSelected}
            maxFiles={sourcesPerProjectLimit}
            limitReachedMessage={sourceLimitReachedMessage}
          />
        </div>

        <div className="project-brain-section">
          <h6 className="project-brain-section-label">Provide Project Context</h6>
          <ManualTagInput
            tags={manualTags}
            onTagAdd={handleTagAdd}
            onTagRemove={handleTagRemove}
          />
        </div>

        <div className="project-brain-actions">
          <Button
            variant="primary"
            className={hasContent ? 'project-brain-continue-btn' : 'project-brain-skip-btn'}
            onClick={handleContinueToProcess}
            disabled={!canContinue}
          >
            {hasContent ? 'Continue' : 'Set up later'}
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'manage' && existingContext) {
    const {
      sources: ctxSources,
      signals: ctxSignals,
      readinessScore: ctxScore,
      readinessBreakdown: ctxBreakdown,
    } = existingContext;

    return (
      <div className="project-brain-step">
        <h2 className="project-brain-title">your project context</h2>
        <p className="project-brain-subtitle">
          Manage the documents and tags that train your AI strategist. Add or remove anything below.
        </p>

        <div className="project-brain-readiness-meter">
          <ContextReadinessMeter score={ctxScore} breakdown={ctxBreakdown} />
        </div>

        <div className="project-brain-section">
          <h6 className="project-brain-section-label">Sources ({ctxSources.length})</h6>
          <div className="project-brain-sources-list">
            {ctxSources.length === 0 && (
              <p className="project-brain-empty-message">No source files uploaded yet.</p>
            )}
            {ctxSources.map((source) => (
              <SourceItem
                key={source.id}
                source={source}
                onDelete={
                  onDeleteSource ? (s) => { onDeleteSource(s.id); } : () => {}
                }
              />
            ))}
          </div>
          {onAddSource && (
            <Button
              variant="link"
              size="sm"
              className="project-brain-add-source-btn p-0 mt-1"
              onClick={onAddSource}
            >
              <PlusLg size={12} className="me-1" />
              Add Source
            </Button>
          )}
        </div>

        <div className="project-brain-section">
          <h6 className="project-brain-section-label">Signals ({ctxSignals.length})</h6>
          <div className="project-brain-signals-list">
            {ctxSignals.length === 0 && (
              <p className="project-brain-empty-message">
                No signals yet. Upload a document or add a tag below.
              </p>
            )}
            {ctxSignals.map((signal) => (
              <SignalChip
                key={signal.id}
                signal={signal}
                sourceLabel={getSourceLabel(signal, existingSourceMap)}
                onDelete={onDeleteSignal ? (s) => { onDeleteSignal(s.id); } : undefined}
              />
            ))}
          </div>
        </div>

        {onAddManualSignal && (
          <div className="project-brain-section">
            <h6 className="project-brain-section-label">Provide Project Context</h6>
            <ManualTagInput
              tags={[]}
              onTagAdd={(label, category) => {
                onAddManualSignal(label, category);
              }}
              onTagRemove={() => {}}
              maxTags={Number.MAX_SAFE_INTEGER}
            />
          </div>
        )}
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="project-brain-step project-brain-step-processing">
        <h2 className="project-brain-processing-title">processing your context</h2>
        <ProgressBar animated striped now={100} className="project-brain-progress-bar" />
        <p className="project-brain-processing-message">{processingMessage}</p>
      </div>
    );
  }

  // Review step
  return (
    <div className="project-brain-step">
      <h2 className="project-brain-title">your project context</h2>
      <p className="project-brain-subtitle">
        Here are the signals we extracted from your documents and tags. You can edit or remove them.
      </p>

      {readinessBreakdown && (
        <div className="project-brain-readiness-meter">
          <ContextReadinessMeter score={readinessScore} breakdown={readinessBreakdown} />
        </div>
      )}

      {signals.length > 0 && (
        <div className="project-brain-signals-section">
          <h6 className="project-brain-signals-heading">Extracted Signals ({signals.length})</h6>
          <div className="project-brain-signals-list">
            {signals.map((signal) => (
              <SignalChip key={signal.id} signal={signal} sourceLabel={getSourceLabel(signal, sourceMap)} />
            ))}
          </div>
        </div>
      )}

      {signals.length === 0 && (
        <p className="project-brain-empty-message">
          {processingError || 'No signals were extracted yet. You can add context later from the sidebar.'}
        </p>
      )}

      <div className="project-brain-actions">
        <Button variant="primary" onClick={() => onContinue(projectName.trim() || 'My First Project')}>
          {continueButtonText}
        </Button>
      </div>
    </div>
  );
};

export default ProjectBrainModal;
